"use client";

import React, { useState } from 'react';
import { Play, X, ChevronRight, ChevronLeft } from 'lucide-react';

interface VideoItem {
  id: string;
  youtubeId: string;
  category: string;
  title: string;
  description: string;
  bgColor: string;
  label: string;
}

const videos: VideoItem[] = [
  {
    id: '1',
    youtubeId: 'csgPtAVyN7U',
    category: 'Desarrolla tu público',
    title: 'Alienta la participación de los visitantes con la recirculación',
    description: 'Aprende estrategias para mantener a tu audiencia comprometida.',
    bgColor: 'bg-[#f0f7f4]', // Light green
    label: 'Lección'
  },
  {
    id: '2',
    youtubeId: 'pEAISN5uZwg',
    category: 'Fortalece tus habilidades',
    title: 'Búsqueda inversa por imagen: Verificar imagen',
    description: 'Herramientas esenciales para el Fact-Checking moderno.',
    bgColor: 'bg-[#f0f4f9]', // Light blue
    label: 'Lección'
  },
  {
    id: '3',
    youtubeId: '1U_5k38u8Tc',
    category: 'Aumenta tus ingresos',
    title: 'Crea una estrategia de recaudación de fondos',
    description: 'Modelos de sostenibilidad para medios independientes.',
    bgColor: 'bg-[#fdf8ef]', // Light yellow/cream
    label: 'Lección'
  }
];

const VideoCarousel = () => {
  const [selectedVideo, setSelectedVideo] = useState<VideoItem | null>(null);

  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="text-primary font-bold uppercase tracking-widest text-xs mb-4 block">Recursos de Google News Initiative</span>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-charcoal">Aprende con los Expertos</h2>
          </div>
          <div className="hidden md:flex gap-2">
            <button className="p-3 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors">
              <ChevronLeft size={24} className="text-charcoal-muted" />
            </button>
            <button className="p-3 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors">
              <ChevronRight size={24} className="text-charcoal-muted" />
            </button>
          </div>
        </div>

        <div className="flex gap-6 overflow-x-auto pb-8 snap-x no-scrollbar">
          {videos.map((video) => (
            <div 
              key={video.id}
              className={`flex-none w-[320px] md:w-[400px] snap-start rounded-[32px] p-8 ${video.bgColor} flex flex-col transition-transform hover:scale-[1.02] duration-300`}
            >
              <h3 className="text-xl font-bold text-charcoal text-center mb-8 min-h-[3rem]">
                {video.category}
              </h3>
              
              <div 
                className="relative aspect-video rounded-2xl overflow-hidden mb-6 cursor-pointer group"
                onClick={() => setSelectedVideo(video)}
              >
                <img 
                  src={`https://img.youtube.com/vi/${video.youtubeId}/maxresdefault.jpg`} 
                  alt={video.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center text-white shadow-xl transform transition-transform group-hover:scale-110">
                    <Play size={32} fill="currentColor" />
                  </div>
                </div>
              </div>

              <div className="mt-auto">
                <span className="text-xs font-bold text-charcoal-muted uppercase tracking-wider mb-2 block">
                  {video.label}
                </span>
                <h4 className="text-lg font-bold text-charcoal leading-tight">
                  {video.title}
                </h4>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-md" 
            onClick={() => setSelectedVideo(null)}
          ></div>
          <div className="relative bg-white rounded-[32px] w-full max-w-4xl shadow-2xl overflow-hidden animate-in zoom-in duration-300">
            <button 
              onClick={() => setSelectedVideo(null)}
              className="absolute top-6 right-6 z-10 p-2 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white rounded-full transition-colors"
            >
              <X size={24} />
            </button>
            
            <div className="aspect-video w-full">
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo.youtubeId}?autoplay=1`}
                title={selectedVideo.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            
            <div className="p-8">
              <span className="text-xs font-bold text-primary uppercase tracking-widest mb-2 block">
                {selectedVideo.label} • {selectedVideo.category}
              </span>
              <h3 className="text-2xl font-bold text-charcoal mb-2">{selectedVideo.title}</h3>
              <p className="text-charcoal-muted">{selectedVideo.description}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default VideoCarousel;